import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktButton } from './marketplaceUi';
import { getStudy } from './marketplaceApi';

/**
 * Shown once, immediately after a study is submitted/paid. Polls study status;
 * results are emailed asynchronously. User controls navigation (no auto-redirect).
 */
const ProcessingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const stateId = (location.state as { studyId?: number } | null)?.studyId;
  const studyId = stateId ?? (params.get('study') ? Number(params.get('study')) : undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!studyId) { navigate('/marketplace/run', { replace: true }); return; }
    let active = true;
    const poll = async () => {
      try {
        const s = await getStudy(studyId);
        // Paid chat returns here after Stripe — send the user into the live chat.
        if (active && s.use_case === 'chat') { navigate(`/marketplace/studies/${studyId}/chat`, { replace: true }); return; }
        if (active && (s.status === 'report_ready' || s.report_status === 'ready')) setReady(true);
      } catch { /* keep polling */ }
    };
    poll();
    const id = setInterval(poll, 8000);
    return () => { active = false; clearInterval(id); };
  }, [studyId, navigate]);

  return (
    <MarketplaceShell maxWidth="max-w-xl">
      <div className="flex flex-col items-center py-16 text-center">
        {ready ? (
          <>
            <CheckCircle2 className="h-12 w-12 text-primary" />
            <h1 className="mt-4 text-[22px] font-medium text-[#111]">Your results are ready</h1>
            <p className="mt-2 text-[13px] text-[#6b6b6b]">We’ve emailed your report. You can run another study any time.</p>
          </>
        ) : (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <h1 className="mt-4 text-[22px] font-medium text-[#111]">Your study is running</h1>
            <p className="mt-2 max-w-md text-[13px] leading-relaxed text-[#6b6b6b]">
              Your Twins are responding now. We’ll email your results in a few minutes —
              you don’t need to wait on this page.
            </p>
          </>
        )}
        <div className="mt-8">
          <MktButton onClick={() => navigate('/marketplace/done')}>See what else you can do →</MktButton>
        </div>
      </div>
    </MarketplaceShell>
  );
};

export default ProcessingScreen;
